import { useLocation } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Tabs, { Tab, TabList, TabPanel } from '@/design_system/Tabs/Tabs';
import Title from '@/design_system/Typography/Title';
import IconChevron from '@/icons/Chevron.svg';
import { getUserWorkshops, hasPermission } from '@/models/user';
import { DefectsTable } from '@/routes/Settings/Defects/DefectsTable';
import { InternalWorkshops } from '@/routes/Settings/InternalWorkshops';
import { Workflows } from '@/routes/Settings/Workflows/Workflows';
import {
  getSettingsBetaPath,
  type LegacySettingsLocationState,
} from '@/routes/SettingsBeta/legacy-settings';
import { useCurrentOrganization, useCurrentSession } from '@/services/auth';
import { useSearchParamState, useSyncSearchParams } from '@/utils/useSearchParams';
import useViewport from '@/utils/useViewport';

import { Actions } from './Actions/Actions';
import { ConditionFormsTable } from './ConditionForms/ConditionFormsTable';
import { ServicesWorkshop } from './ServicesWorkshop/ServicesWorkshop';
import { Users } from './Users/Users';
import { Companies } from './Companies';
import { ExternalStores } from './ExternalStores';
import { EntityGroups } from './Groups';
import { SparePartTypes } from './SparePartTypes';
import Stores from './Stores';
import Workshop from './Workshop';

const Settings = () => {
  const { t } = useLingui();
  const { isMobile } = useViewport();
  const location = useLocation();
  const settingsBetaState = location.state as LegacySettingsLocationState | null;
  const cameFromSettingsBeta = !!settingsBetaState?.fromSettingsBeta;
  const { currentSession } = useCurrentSession();
  const [currentOrganization] = useCurrentOrganization();

  const canManageUsers = hasPermission(currentSession, 'manage_users', {
    organizationId: currentOrganization?.id,
    entityId: null,
  });

  const canManageStores = hasPermission(currentSession, 'manage_entities', {
    organizationId: currentOrganization?.id,
    entityId: null,
  });

  const canManageWorkshops = hasPermission(currentSession, 'manage_entities', {
    organizationId: currentOrganization?.id,
    entityId: null,
  });

  const canManageOrganization = hasPermission(currentSession, 'manage_organization', {
    organizationId: currentOrganization?.id,
    entityId: null,
  });

  const canManageBilling = hasPermission(currentSession, 'manage_billing', {
    organizationId: currentOrganization?.id,
    entityId: null,
  });

  const userWorkshops = getUserWorkshops(currentSession);
  const canManageWorkshop =
    userWorkshops.length === 1 &&
    hasPermission(currentSession, 'manage_entity', {
      entityId: userWorkshops[0].id,
    });

  const canManageEntities = hasPermission(currentSession, 'manage_entities', {
    organizationId: currentOrganization?.id,
    entityId: null,
  });

  const initialTab = canManageUsers
    ? 'users'
    : canManageStores
      ? 'stores'
      : canManageWorkshops
        ? 'internal-workshops'
        : canManageOrganization
          ? 'action-types'
          : canManageBilling
            ? 'companies'
            : canManageWorkshop
              ? 'workshop'
              : '';

  const [tab, setTab, syncTab] = useSearchParamState<string>('settings', 'tab', initialTab);

  useSyncSearchParams([syncTab]);

  return (
    <>
      <div className="header">
        <Stack row alignItems="center" gap="1rem">
          <Title size="200" as="h1">
            <Stack row alignItems="center" gap="1rem">
              {isMobile && (
                <Button iconOnly variant="secondary" to="/menu">
                  <IconChevron left />
                </Button>
              )}
              <Trans id="settings.title">Settings</Trans>
            </Stack>
          </Title>
          {cameFromSettingsBeta && (
            <Button
              variant="link-subtle"
              size="compact"
              to={settingsBetaState?.backTo ?? getSettingsBetaPath(tab)}
            >
              <Trans id="settings.back-to-beta">Back to Settings (Beta)</Trans>
            </Button>
          )}
        </Stack>
      </div>

      <div className="container container--list">
        <Stack gap="40px">
          <Tabs selectedKey={tab} onSelectionChange={setTab}>
            <TabList aria-label={t({ id: 'settings.tabs.label', message: 'Settings panels' })}>
              {canManageUsers && (
                <Tab id="users">
                  <Trans id="settings.tabs.users">Users</Trans>
                </Tab>
              )}
              {canManageStores && (
                <>
                  <Tab id="stores">
                    <Trans id="settings.tabs.stores">Stores</Trans>
                  </Tab>
                  <Tab id="external-stores">
                    <Trans id="settings.tabs.external-stores">External Stores</Trans>
                  </Tab>
                </>
              )}
              {canManageWorkshops && (
                <Tab id="internal-workshops">
                  <Trans id="settings.tabs.internal-workshops">Internal Workshops</Trans>
                </Tab>
              )}
              {canManageEntities && (
                <Tab id="groups">
                  <Trans id="settings.tabs.groups">Groups</Trans>
                </Tab>
              )}
              {canManageOrganization && (
                <>
                  <Tab id="actions">
                    <Trans id="settings.tabs.actions">Actions</Trans>
                  </Tab>
                  <Tab id="spare-part-types">
                    <Trans id="settings.tabs.spare-part-types">Spare parts</Trans>
                  </Tab>
                  <Tab id="defect-types">
                    <Trans id="settings.tabs.defect-types">Defects</Trans>
                  </Tab>
                  <Tab id="condition-forms">
                    <Trans id="settings.tabs.condition-forms">Condition Forms</Trans>
                  </Tab>
                  <Tab id="workflows">
                    <Trans id="settings.tabs.workflows">Workflows</Trans>
                  </Tab>
                </>
              )}
              {canManageBilling && (
                <Tab id="companies">
                  <Trans id="settings.tabs.companies">Companies</Trans>
                </Tab>
              )}
              {canManageWorkshop && (
                <>
                  <Tab id="workshop">
                    <Trans id="settings.tabs.workshop">Workshop</Trans>
                  </Tab>
                  <Tab id="services-workshop">
                    <Trans id="settings.tabs.services-workshop">Services</Trans>
                  </Tab>
                </>
              )}
            </TabList>
            <TabPanel id="companies">
              <Companies />
            </TabPanel>
            <TabPanel id="users">
              <Users />
            </TabPanel>
            <TabPanel id="stores">
              <Stores />
            </TabPanel>
            <TabPanel id="external-stores">
              <ExternalStores />
            </TabPanel>
            <TabPanel id="internal-workshops">
              <InternalWorkshops />
            </TabPanel>
            <TabPanel id="groups">
              <EntityGroups />
            </TabPanel>
            <TabPanel id="workshop">
              <Workshop />
            </TabPanel>
            <TabPanel id="actions">
              <Actions />
            </TabPanel>
            <TabPanel id="spare-part-types">
              <SparePartTypes />
            </TabPanel>
            <TabPanel id="services-workshop">
              <ServicesWorkshop />
            </TabPanel>
            <TabPanel id="defect-types">
              <DefectsTable />
            </TabPanel>
            <TabPanel id="condition-forms">
              <ConditionFormsTable />
            </TabPanel>
            <TabPanel id="workflows">
              <Workflows />
            </TabPanel>
          </Tabs>
        </Stack>
      </div>
    </>
  );
};

export default Settings;
